import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Layers, ThumbsUp, AlertTriangle, Lightbulb } from "lucide-react";
import { cn } from "@/lib/utils";
import { getArchetypeConfig } from "@/lib/archetypeConfig";

interface Option {
  option_id: string;
  label: string;
  archetype?: string;
  archetype_label?: string;
  strategic_goal?: string;
  key_advantages?: string[];
  key_risks?: string[];
  why_fits?: string;
  when_this_option_fits?: string;
  hypothesis_link?: string;
  total_points?: number;
  summary?: string;
  pros_short?: string[];
  cons_short?: string[];
}

interface OptionsCompactProps {
  data: {
    options?: Option[];
  };
  primaryHypothesis?: string;
}

export function OptionsCompact({ data, primaryHypothesis }: OptionsCompactProps) {
  if (!data?.options || data.options.length === 0) return null;

  return (
    <Card id="options" className="scroll-mt-24">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Layers className="h-5 w-5 text-primary" />
          Optionen
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Jede Option: 1 Satz Kernlogik, 1 Vorteil, 1 Risiko, Hypothesen-Bezug
        </p>
        <Badge variant="secondary" className="text-[10px] w-fit mt-2">
          Aus Phase 2 übernommen
        </Badge>
      </CardHeader>
      <CardContent>
        {/* Kompakte Darstellung: 1 Satz + 1 Vorteil + 1 Risiko + Hypothesen-Bezug */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 user-input-compact">
          {data.options.map((option, index) => {
            const advantages = option.key_advantages || option.pros_short || [];
            const risks = option.key_risks || option.cons_short || [];
            const goal = option.strategic_goal || option.summary || "";
            const optionId = option.option_id || `option-${index}`;
            const hypothesisLink = option.hypothesis_link || option.when_this_option_fits;
            
            // Get archetype styling from central config
            const archetype = option.archetype || "low_risk";
            const archetypeConfig = getArchetypeConfig(archetype);
            const archetypeLabel = option.archetype_label || archetypeConfig?.label || archetype;
            const isWarning = archetypeConfig?.isWarning || false;
            
            return (
              <div 
                key={optionId}
                className={cn(
                  "border rounded-lg p-3 bg-card flex flex-col",
                  archetypeConfig?.borderColor || "border-border"
                )}
              >
                {/* Name + Archetype Badge */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h4 className="font-semibold text-sm">{option.label || `Option ${index + 1}`}</h4>
                  {archetypeLabel && archetypeConfig && (
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px] shrink-0",
                        archetypeConfig.color
                      )}
                    >
                      {isWarning && <AlertTriangle className="h-2.5 w-2.5 mr-1" />}
                      {archetypeLabel}
                    </Badge>
                  )}
                </div>

                {/* 1 Satz Kernlogik */}
                {goal && (
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                    {goal}
                  </p>
                )}

                {/* 1 Vorteil */}
                {advantages.length > 0 && (
                  <div className="flex items-start gap-2 mb-1">
                    <ThumbsUp className="h-3 w-3 mt-0.5 shrink-0 text-green-500" />
                    <span className="text-xs text-green-600 dark:text-green-400 line-clamp-1">
                      {advantages[0]}
                    </span>
                  </div>
                )}

                {/* 1 Risiko */}
                {risks.length > 0 && (
                  <div className="flex items-start gap-2 mb-2">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-amber-500" />
                    <span className="text-xs text-amber-600 dark:text-amber-400 line-clamp-1">
                      {risks[0]}
                    </span>
                  </div>
                )}

                {/* Hypothesen-Bezug - PROMINENT */}
                {hypothesisLink && (
                  <div className="mt-auto pt-2 border-t border-dashed">
                    <div className="flex items-start gap-1.5">
                      <Lightbulb className="h-3 w-3 mt-0.5 shrink-0 text-primary" />
                      <p className="text-[10px] text-primary/80 italic line-clamp-2">
                        <span className="font-semibold">Testet:</span> {hypothesisLink}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Hypothesen-Hinweis */}
        {primaryHypothesis && (
          <p className="text-xs text-muted-foreground mt-4 pt-3 border-t text-center">
            <span className="font-medium">Kernhypothese:</span> {primaryHypothesis}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
